import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Calculator, FileText, Handshake, MapPinned, Building2, Package, FileBarChart, Users, Settings, LogOut, RefreshCw, TrendingUp, Plus, Target, Wrench, BellRing, HardHat, Receipt } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SalesBackground } from "@/components/SalesBackground";
import { Button } from "@/components/ui/button";
import { ReminderBell } from "@/components/Reminders";

const NAV = [
  ["/", "Dashboard", LayoutDashboard],
  ["/costings", "Kalkulasi Cost", Calculator],
  ["/quotations", "Penawaran", FileText],
  ["/nego", "Nego Penawaran", Handshake],
  ["/proforma", "Proforma Invoice", Receipt],
  ["/activities", "Sales Activity", MapPinned],
  ["/customers", "Customer", Building2],
  ["/products", "Barang / Jasa", Package],
  ["/reports", "Laporan", FileBarChart],
  ["/quote-reminders", "Reminder Penawaran", BellRing],
  ["/maintenance", "Maintenance", Wrench],
  ["/forecast", "Forecast & Budget", Target],
  ["/tech", "Teknisi", HardHat],
];
const TECH_NAV = [["/tech", "Teknisi", HardHat]];
const ADMIN_NAV = [["/users", "Users", Users], ["/settings", "Pengaturan", Settings]];

const Item = ({ to, label, Icon }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={`nav-${to === "/" ? "dashboard" : to.slice(1)}`}
    className={({ isActive }) =>
      `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#1E3A8A] text-white shadow-md shadow-blue-900/20" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`
    }
  >
    <Icon className="h-[18px] w-[18px]" />
    {label}
  </NavLink>
);

export default function Layout() {
  const { user, logout, rate, loadRate, isAdmin } = useAuth();
  const nav = useNavigate();
  const tech = user?.role === "teknisi";
  const MENU = tech ? TECH_NAV : NAV;
  return (
    <div className="relative min-h-screen">
      <SalesBackground />
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-64 flex-col border-r border-slate-200 bg-white/90 backdrop-blur-xl lg:flex">
        <div className="flex items-center gap-2.5 px-6 py-6">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white shadow-lg"><TrendingUp className="h-5 w-5" /></div>
          <div>
            <p className="font-heading text-xl font-bold leading-none">JLY<span className="text-[#0284C7]">sales</span></p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">by Jlybalance</p>
            {user?.company?.name && <p className="mt-1.5 max-w-[150px] truncate text-xs font-semibold text-blue-900" title={user.company.name} data-testid="sidebar-company-name">{user.company.name}</p>}
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3">
          {MENU.map(([to, l, I]) => <Item key={to} to={to} label={l} Icon={I} />)}
          {isAdmin && (
            <>
              <p className="px-3 pt-5 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Admin</p>
              {ADMIN_NAV.map(([to, l, I]) => <Item key={to} to={to} label={l} Icon={I} />)}
            </>
          )}
          {user?.platform_role === "superadmin" && (
            <>
              <p className="px-3 pt-5 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Super Admin</p>
              <Item to="/platform" label="Perusahaan Penyewa" Icon={Building2} />
            </>
          )}
        </nav>
        <div className="border-t border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <NavLink to="/profile" className="flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 hover:bg-slate-100 transition-colors" data-testid="nav-profile" title="Profil Saya">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-blue-50 font-heading font-bold text-blue-900">{user?.name?.[0]}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold" data-testid="current-user-name">{user?.name}</p>
                <p className="text-[11px] uppercase tracking-wider text-slate-400" data-testid="current-user-role">{user?.role} • Profil</p>
              </div>
            </NavLink>
            <Button size="icon" variant="ghost" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </aside>
      <div className="relative z-10 min-w-0 lg:pl-64">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/80 px-6 py-3 backdrop-blur-xl">
          <div className="flex items-center gap-2 lg:hidden">
            <p className="font-heading text-lg font-bold">JLY<span className="text-[#0284C7]">sales</span></p>
            <select className="ml-2 h-9 rounded-md border px-2 text-sm" onChange={(e) => nav(e.target.value)} data-testid="mobile-nav-select">
              {[...MENU, ...(isAdmin ? ADMIN_NAV : []), ["/profile", "Profil Saya"]].map(([to, l]) => <option key={to} value={to}>{l}</option>)}
            </select>
          </div>
          <div className="hidden lg:block text-sm text-slate-500">Selamat datang, <b className="text-slate-800">{user?.name}</b></div>
          <div className="flex items-center gap-2">
            {!tech && <ReminderBell />}
            <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs" data-testid="rate-indicator">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-slate-500">USD/IDR</span>
              <span className="font-num font-semibold" data-testid="rate-value">{rate ? Math.round(rate.rate).toLocaleString("id-ID") : "..."}</span>
              <button onClick={() => loadRate(true)} title={rate?.source} data-testid="rate-refresh-btn"><RefreshCw className="h-3.5 w-3.5 text-slate-400 hover:text-blue-800" /></button>
            </div>
            {!tech && <Button size="sm" onClick={() => nav("/costings/new")} data-testid="header-new-costing-btn"><Plus className="h-4 w-4 mr-1" />Kalkulasi Baru</Button>}
            <Button size="icon" variant="ghost" className="lg:hidden" onClick={logout}><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        {user?.company?.expired && <div className="mx-5 mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 md:mx-10" data-testid="subscription-expired-banner">Masa aktif langganan <b>{user.company.name}</b> habis sejak {user.company.active_until}. Data hanya bisa dilihat — hubungi pemilik aplikasi untuk perpanjangan.</div>}
        <main className="min-w-0 max-w-[1500px] overflow-x-auto px-5 py-8 md:px-10"><Outlet /></main>
      </div>
    </div>
  );
}
